import { createContext, useContext } from 'react';
import type { Me, UserPreferences } from '@/api/session';
import type { Feature, WorkspaceDetails } from '@/api/workspace';

export interface CurrentWorkspace {
  workspaceId: string;
  workspace: WorkspaceDetails;
  features: Set<Feature>;
  user: Me;
  prefs: UserPreferences;
  isAdmin: boolean;
  isOwner: boolean;
  isManager: boolean;
  hasFeature: (feature: Feature) => boolean;
  canSeeAmounts: boolean;
}

export const WorkspaceCtx = createContext<CurrentWorkspace | null>(null);

/** Aktueller Workspace, Benutzer und Präferenzen (nur innerhalb des App-Layouts verfügbar). */
export function useCurrentWorkspace(): CurrentWorkspace {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) {
    throw new Error('useCurrentWorkspace außerhalb des WorkspaceProviders');
  }
  return ctx;
}

export function buildWorkspaceContext(
  workspace: WorkspaceDetails,
  features: Feature[],
  user: Me,
): CurrentWorkspace {
  const set = new Set(features);
  const { me, settings } = workspace;
  const audience = settings.permissions.seeAmounts;
  const canSeeAmounts =
    audience === 'EVERYONE' || me.admin || (audience === 'MANAGERS' && me.teamManager);
  return {
    workspaceId: workspace.id,
    workspace,
    features: set,
    user,
    prefs: user.preferences,
    isAdmin: me.admin,
    isOwner: me.owner,
    isManager: me.admin || me.teamManager,
    hasFeature: (f) => set.has(f),
    canSeeAmounts,
  };
}
